/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, { useState } from "react";

import bcrypt from "bcryptjs";
import { useParams } from "react-router-dom";

import openToast from "utils/openToast";
import usePreviousRoute from "utils/usePreviousRoute";
import { verifyStrongPassword } from "utils/validator";

import usePermissions from "hooks/usePermissions";
import useTryCatch from "hooks/useTryCatch";
import { createAdmin, getAdmin, updateAdmin } from "lib/v2/adminsApi";

import { AdminCredentials, AdminFormPageHeader, AdminInfo } from "./Components";

import { getEnvs, useEnvs } from "state/EnvironmentsContext/EnvContext";
import MetaTitle from "components/ui/MetaTitle";

const AdminForm = ({ permissions, routeMap, data: Admin, pageHeaderProps }) => {
  const goBack = usePreviousRoute(routeMap.base);
  const [isLoading, setLoading] = useState(false);
  const { id } = useParams();
  const [formType, setFormType] = React.useState("create"); // create or edit
  const formPermissions = usePermissions(permissions, formType);

  const preFillForm = useTryCatch(async () => {
    let data = Admin;
    if (!data || !data.username) {
      const api = await getAdmin(id);
      if (!api.ok) return goBack();
      data = api.data;
    }

    setUsername(data.username);
    setImage(data.image);
    setActive(data.activated);
    setAdminRoleId(data.admin_role_id);
    setEmail(data.email);
    setCountryCode(data.country_code);
    setPhoneNumber(data.contact_number);
    setFirstName(data.first_name);
    setLastName(data.last_name);
  }, setLoading);

  React.useEffect(() => {
    if (id === null || id === undefined || id === "new") {
      setFormType("create");
    } else {
      preFillForm();
      setFormType("edit");
    }
  }, [id]);

  const [image, setImage] = useState("");
  const [isActive, setActive] = useState(true);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [countryCode, setCountryCode] = useState(
    getEnvs((state) => state.country_code),
  );

  const [isPasswordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [adminRoleId, setAdminRoleId] = useState("");
  const PASSWORD_SALT = useEnvs((state) => state.password_salt);

  const onSubmit = useTryCatch(
    async (e) => {
      e.preventDefault();

      if (!username) {
        return openToast("Username is required", "error");
      }

      if (email.length === 0) {
        return openToast("Email is required", "error");
      }

      if (phoneNumber.length === 0 || countryCode.length === 0) {
        return openToast("Phone Number is required", "error");
      }

      if (!adminRoleId) {
        return openToast("Please select an admin role", "error");
      }

      let api, hashPassword;

      const body = {
        username,
        image,
        activated: isActive,
        email,
        contact_number: phoneNumber,
        country_code: countryCode,
        first_name: firstName,
        last_name: lastName,
      };

      if (adminRoleId) {
        body.admin_role_id = adminRoleId;
      }

      if (formType === "create") {
        const passwordValidMessage = verifyStrongPassword(password);
        if (passwordValidMessage !== true) {
          setLoading(false);
          return openToast(passwordValidMessage, "error");
        }

        hashPassword = bcrypt.hashSync(password, PASSWORD_SALT);

        body.password = hashPassword;
        api = await createAdmin(body);
      } else {
        if (password.length > 0) {
          const passwordValidMessage = verifyStrongPassword(password);
          if (passwordValidMessage !== true) {
            setLoading(false);
            return openToast(passwordValidMessage, "error");
          }

          hashPassword = bcrypt.hashSync(password, PASSWORD_SALT);

          body.password = hashPassword;
        }

        api = await updateAdmin(body, id);
      }

      if (api.status === "SUCCESS") {
        goBack();
      }
    },
    {
      setLoading,
    },
  );

  return (
    <>
      <MetaTitle
        title={`${
          formType === "create" ? "Create Admin" : firstName + " " + lastName
        } - Admins`}
        test-id="bKTz42nm"
      />

      <form test-id="zfCb6fXs">
        <AdminFormPageHeader
          formType={formType}
          setLoading={(s) => setLoading(s)}
          isLoading={isLoading}
          onSubmit={onSubmit}
          formPermissions={formPermissions}
          pageHeaderProps={pageHeaderProps}
          goBack={goBack}
          test-id="C-sIYs51"
        />

        <div
          className="flex flex-col md:flex-row justify-between lg:w-[890px]  space-y-4 md:space-y-0 md:space-x-4 text-white"
          test-id="Y2kXBhj-"
        >
          <AdminInfo
            image={image}
            setImage={(s) => setImage(s)}
            setActive={(s) => setActive(s)}
            isActive={isActive}
            adminRoleId={adminRoleId}
            setAdminRoleId={(s) => setAdminRoleId(s)}
            formPermissions={formPermissions}
            setEmail={(s) => setEmail(s)}
            setPhoneNumber={(s) => setPhoneNumber(s)}
            setCountryCode={(s) => setCountryCode(s)}
            email={email}
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            lastName={lastName}
            firstName={firstName}
            setLastName={(s) => setLastName(s)}
            setFirstName={(s) => setFirstName(s)}
            setUsername={(s) => setUsername(s)}
            test-id="xlU-ct6H"
          />

          <AdminCredentials
            username={username}
            setUsername={(s) => setUsername(s)}
            password={password}
            setPassword={(s) => setPassword(s)}
            isPasswordEnabled={isPasswordEnabled}
            setPasswordEnabled={(b) => setPasswordEnabled(b)}
            formPermissions={formPermissions}
            test-id="Rn_iqeof"
          />
        </div>
      </form>
    </>
  );
};

export default AdminForm;
